"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import {
    buildLocalizedPath,
    getLanguageDirection,
    LANGUAGE_LABELS,
    SUPPORTED_LANGUAGES,
    type Language,
} from "@/lib/i18n";
import { useTranslations } from "@/components/i18n/use-translations";

const STORAGE_KEY = "preferred-language";
const EXIT_MS = 200;

function prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function beginLocaleExit() {
    if (prefersReducedMotion()) {
        return;
    }
    document.documentElement.dataset.localeSwitch = "exit";
    window.dispatchEvent(new Event("scamatrix:locale-exit"));
}

export function LanguageSwitcher({ currentLang }: { currentLang: Language }) {
    const pathname = usePathname();
    const router = useRouter();
    const { messages } = useTranslations();
    const [isOpen, setIsOpen] = useState(false);
    const [switching, setSwitching] = useState(false);

    const items = useMemo(() => {
        return SUPPORTED_LANGUAGES.map((lang) => ({
            lang,
            href: buildLocalizedPath(pathname, lang),
            label: messages.languageSwitcher.languageNames[lang] ?? LANGUAGE_LABELS[lang].nativeLabel,
        }));
    }, [messages.languageSwitcher.languageNames, pathname]);

    const handleSelect = (lang: Language, href: string) => {
        if (typeof window === "undefined" || lang === currentLang || switching) {
            return;
        }

        window.localStorage.setItem(STORAGE_KEY, lang);
        document.cookie = `${STORAGE_KEY}=${lang}; path=/; max-age=31536000`;
        setIsOpen(false);
        setSwitching(true);
        beginLocaleExit();

        const delay = prefersReducedMotion() ? 0 : EXIT_MS;
        window.setTimeout(() => {
            document.documentElement.lang = lang;
            document.documentElement.dir = getLanguageDirection(lang);
            router.push(href);
            setSwitching(false);
        }, delay);
    };

    const toggleOpen = () => setIsOpen((prev) => !prev);
    const closeMenu = () => setIsOpen(false);

    return (
        <div
            className="relative flex items-center"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={closeMenu}
        >
            <button
                type="button"
                onClick={toggleOpen}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700/60 bg-slate-900/60 text-xs font-semibold text-gray-200 transition-transform hover:scale-105"
                aria-label={messages.languageSwitcher.label}
                aria-expanded={isOpen}
            >
                {currentLang.toUpperCase()}
            </button>
            <div
                className={`absolute right-0 top-full z-20 min-w-[220px] overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900/95 p-3 text-xs text-gray-300 shadow-lg backdrop-blur-sm transition-all duration-200 ${
                    isOpen ? "pointer-events-auto scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
                }`}
            >
                <span className="block text-[11px] uppercase tracking-[0.2em] text-gray-400">
                    {messages.languageSwitcher.label}
                </span>
                <div className="mt-2 flex flex-wrap gap-2">
                    {items.map((item) => (
                        <Link
                            key={item.lang}
                            href={item.href}
                            onClick={(event) => {
                                event.preventDefault();
                                handleSelect(item.lang, item.href);
                            }}
                            className={`rounded-full px-2 py-1 transition-colors ${
                                currentLang === item.lang
                                    ? "bg-cyan-500/20 text-cyan-200"
                                    : "text-gray-300 hover:text-white"
                            }`}
                            aria-current={currentLang === item.lang ? "page" : undefined}
                        >
                            <span className="font-semibold">{item.lang.toUpperCase()}</span>
                            <span className="ml-1 mx-1 text-[11px] text-gray-400">{item.label}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
